import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Lock, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { authFetch, setStoredUser } from "@/lib/auth";

const GRADES = ["Grade 8", "Grade 9", "Grade 10", "Grade 11", "Grade 12", "Undergraduate", "Postgraduate", "Career Professional"];
const LOCATIONS = [
  { value: "International", label: "International - Study Abroad" },
  { value: "Domestic", label: "Domestic - Study in India" },
  { value: "Job Seeker", label: "Job Seeker" },
];
const INDUSTRIES = ["Technology & IT", "Finance & Banking", "Healthcare & Pharma", "Marketing & Media", "Education & Research", "Design & Creative"];

const inputCls = "w-full rounded-2xl border border-ink/15 bg-cream px-4 py-3 text-sm outline-none focus:border-iqblue focus:ring-4 focus:ring-iqblue/15 transition-all placeholder:text-ink/35";
const labelCls = "block text-[10px] font-bold tracking-[0.18em] uppercase text-ink/45 mb-2";

const toForm = (u) => ({
  name: u?.name || "",
  mobile: u?.mobile || "",
  grade: u?.grade || "",
  school: u?.school || "",
  study_location: u?.study_location || "",
  industry: u?.industry || "",
});

export default function ProfileEditModal({ open, onClose, user, onSaved }) {
  const [form, setForm] = useState(toForm(user));
  const [busy, setBusy] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const isPro = form.grade === "Career Professional";

  useEffect(() => {
    if (!open) return;
    setForm(toForm(user));
    let active = true;
    authFetch("/api/auth/me").then(async (res) => {
      if (active && res.ok) setForm(toForm(await res.json()));
    });
    return () => { active = false; };
  }, [open, user]);

  const set = (key) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [key]: value, ...(key === "grade" && value !== "Career Professional" ? { industry: "" } : {}) }));
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, industry: isPro ? form.industry || null : null };
      const res = await authFetch("/api/auth/profile", { method: "PUT", body: JSON.stringify(payload) });
      const data = await res.json();
      if (!res.ok) {
        toast.error(typeof data.detail === "string" ? data.detail : "Could not update your profile");
        return;
      }
      setStoredUser(data.user);
      onSaved?.(data.user);
      toast.success("Profile updated");
      onClose();
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }
    setPwBusy(true);
    try {
      const res = await authFetch("/api/auth/change-password", {
        method: "POST",
        body: JSON.stringify({ current_password: pw.current_password, new_password: pw.new_password }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(typeof data.detail === "string" ? data.detail : "Could not change password");
        return;
      }
      setPw({ current_password: "", new_password: "", confirm_password: "" });
      toast.success("Password updated");
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[85] flex items-center justify-center p-4">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-ink/70 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            data-testid="profile-edit-modal"
            initial={{ opacity: 0, y: 32, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-paper rounded-[1.8rem] shadow-2xl p-7 sm:p-8"
          >
            <button onClick={onClose} data-testid="profile-edit-close" className="absolute top-4 right-4 h-9 w-9 rounded-full bg-ink/5 hover:bg-ink/10 flex items-center justify-center transition-colors" aria-label="Close">
              <X className="h-4 w-4" />
            </button>
            <span className="inline-flex h-11 w-11 rounded-2xl bg-iqblue/10 items-center justify-center">
              <Pencil className="h-5 w-5 text-iqblue" />
            </span>
            <h3 className="font-display font-bold text-2xl tracking-tight mt-5">Edit your profile</h3>
            <p className="mt-2 text-sm text-ink/60">Keep your details current so reports and recommendations stay relevant.</p>

            <form onSubmit={save} className="mt-6 grid sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls} htmlFor="profile-name">Full name</label>
                <input id="profile-name" data-testid="profile-name-input" value={form.name} onChange={set("name")} required minLength={2} maxLength={80} className={inputCls} />
              </div>
              <div>
                <label className={labelCls} htmlFor="profile-mobile">Mobile</label>
                <input id="profile-mobile" data-testid="profile-mobile-input" type="tel" value={form.mobile} onChange={set("mobile")} required className={inputCls} />
              </div>
              <div>
                <label className={labelCls} htmlFor="profile-grade">Class / stage</label>
                <select id="profile-grade" data-testid="profile-grade-select" value={form.grade} onChange={set("grade")} className={inputCls} required>
                  <option value="">Select class / stage</option>
                  {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls} htmlFor="profile-location">Future Plans</label>
                <select id="profile-location" data-testid="profile-location-select" value={form.study_location} onChange={set("study_location")} className={inputCls} required>
                  <option value="">Select future plan</option>
                  {LOCATIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls} htmlFor="profile-school">School / college / organization</label>
                <input id="profile-school" data-testid="profile-school-input" value={form.school} onChange={set("school")} required minLength={2} maxLength={120} className={inputCls} />
              </div>
              {isPro && (
                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="profile-industry">Industry</label>
                  <select id="profile-industry" data-testid="profile-industry-select" value={form.industry} onChange={set("industry")} className={inputCls}>
                    <option value="">Select your industry</option>
                    {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
              )}
              <div className="sm:col-span-2 rounded-2xl bg-cream border border-ink/10 px-4 py-3" data-testid="profile-email-readonly">
                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-ink/40">Sign-in email</div>
                <div className="mt-1 text-sm font-semibold text-ink/75">{user?.email}</div>
              </div>
              <button type="submit" disabled={busy || form.name.trim().length < 2 || form.mobile.trim().length < 8 || !form.grade || !form.study_location || form.school.trim().length < 2} data-testid="profile-save-button" className="sm:col-span-2 w-full flex items-center justify-center gap-2 bg-ink text-paper font-semibold py-4 rounded-full hover:bg-iqblue disabled:opacity-60 transition-colors duration-300">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save profile"}
              </button>
            </form>
            <form onSubmit={changePassword} className="mt-7 border-t border-ink/10 pt-6" data-testid="password-change-form">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 rounded-2xl bg-iqpink/10 items-center justify-center"><Lock className="h-4 w-4 text-iqpink" /></span>
                <div>
                  <h4 className="font-display font-bold text-lg tracking-tight">Change password</h4>
                  <p className="text-xs text-ink/50 mt-0.5">Confirm your current password, then choose a new one.</p>
                </div>
              </div>
              <div className="mt-5 grid sm:grid-cols-3 gap-3">
                <input data-testid="current-password-input" type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} placeholder="Current password" required className={inputCls} autoComplete="current-password" />
                <input data-testid="new-password-input" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} placeholder="New password" required minLength={6} className={inputCls} autoComplete="new-password" />
                <input data-testid="confirm-password-input" type="password" value={pw.confirm_password} onChange={(e) => setPw({ ...pw, confirm_password: e.target.value })} placeholder="Confirm new password" required minLength={6} className={inputCls} autoComplete="new-password" />
              </div>
              <button type="submit" disabled={pwBusy || !pw.current_password || pw.new_password.length < 6 || pw.new_password !== pw.confirm_password} data-testid="password-change-button" className="mt-4 w-full sm:w-auto flex items-center justify-center gap-2 border border-ink/20 font-semibold px-6 py-3 rounded-full hover:bg-ink hover:text-paper disabled:opacity-60 transition-colors duration-300">
                {pwBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
              </button>
            </form>

          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
